import { ACTIVITIES } from 'constants/Entities';

/** Origin segment for activity details opened from Companies/Contacts widgets. */
export const ACTIVITY_DETAIL_ORIGIN_WIDGET = 'widget';

/**
 * Builds the `extraId` segment stored on entityDetail actions/state.
 * Activities: `{activityType}` or `{activityType}-{origin}`.
 * Other entities: `origin` when provided.
 */
export const buildEntityDetailExtraId = ({ activityType, origin } = {}) => {
    const hasType = activityType != null && activityType !== '';
    if (hasType && origin) return `${activityType}-${origin}`;
    if (hasType) return String(activityType);
    if (origin) return String(origin);
    return null;
};

/**
 * Builds the Redux entityDetail tab key.
 * Activities: `activities-{id}-{activityType}[-{origin}]`
 * Other entities: `{entity}-{id}` (optional `-{origin}` when provided)
 */
export const buildEntityDetailTabKey = (entityName, id, { activityType, origin } = {}) => {
    if (entityName == null || entityName === '') return '';
    if (id == null || id === '') return String(entityName);

    let key = `${entityName}-${id}`;
    const extraId = buildEntityDetailExtraId({
        activityType: entityName === ACTIVITIES.entity ? activityType : undefined,
        origin,
    });
    if (extraId) key += `-${extraId}`;
    return key;
};

/**
 * Parses a Redux entityDetail tab key into segments.
 */
export const parseEntityDetailTabKey = (tabKey) => {
    if (!tabKey) {
        return { entity: null, id: null, activityType: null, origin: null, extraId: null };
    }

    const parts = String(tabKey).split('-');
    const entity = parts[0] || null;
    const id = parts[1] || null;

    if (entity === ACTIVITIES.entity) {
        const activityType = parts[2] || null;
        const origin = parts[3] || null;
        const extraId = buildEntityDetailExtraId({ activityType, origin });
        return { entity, id, activityType, origin, extraId };
    }

    const extraId = parts.length > 2 ? parts.slice(2).join('-') : null;
    return {
        entity,
        id,
        activityType: null,
        origin: extraId,
        extraId,
    };
};

/**
 * True when any open tab matches this entity/id (and activityType when provided).
 * Ignores origin so list rows highlight for both list and widget tabs.
 */
export const isEntityDetailTabOpen = (tabs, entityName, id, { activityType } = {}) => {
    if (!tabs || id == null) return false;
    const baseKey = buildEntityDetailTabKey(entityName, id, { activityType });
    return Object.keys(tabs).some((key) => key === baseKey || key.startsWith(`${baseKey}-`));
};

/**
 * Resolves extraId for updates targeting entity+id, preferring the active detail.
 */
export const resolveEntityDetailExtraId = (entityDetail, entityName, id) => {
    if (!entityDetail || id == null) return null;

    const activeKey = entityDetail.active;
    const activeDetail = activeKey ? entityDetail[activeKey] : null;
    if (
        activeDetail?.entity === entityName &&
        String(activeDetail?.id) === String(id) &&
        activeDetail.extraId != null
    ) {
        return activeDetail.extraId;
    }

    const matchingKey = Object.keys(entityDetail.tabs || {}).find((key) => {
        const parsed = parseEntityDetailTabKey(key);
        return parsed.entity === entityName && String(parsed.id) === String(id);
    });

    if (!matchingKey) return null;
    return parseEntityDetailTabKey(matchingKey).extraId;
};
